(() => {
  const instanceKey = Symbol.for('ron.home.motion');
  const sections = [
    ['.ron-always-next', [
      ['.ron-hero-label, .ron-hero-caption'],
      ['.ron-hero-content > h1', 'headline', 1],
      ['.ron-hero-content > p', 'rise', 1],
      ['.ron-intro-strip', 'rule'],
    ]],
    ['.ron-rotation-section', [['.ron-rotation-heading h2', 'headline'], ['.ron-rotation-heading .ron-merch-eyebrow']]],
    ['.ron-editorial', [
      ['.ron-edit-folio', 'rule'],
      ['.ron-edit-header h2', 'headline'],
      ['.ron-edit-note, .ron-edit-caption'],
      ['.ron-edit-story-lead .ron-edit-image', 'campaign'],
      ['.ron-edit-story-secondary .ron-edit-image', 'campaign', 1],
    ]],
    ['.ron-discovery', [['.discovery-heading h2', 'headline'], ['.discovery-card', 'image']]],
    ['.ron-ground-section', [['.ron-ground-heading h2', 'headline']]],
    ['.ron-inside', [
      ['.inside-heading h2', 'headline'],
      ['.inside-heading-note, .inside-visit, .inside-journal-copy'],
      ['.inside-place-image', 'campaign'],
      ['.inside-journal-image', 'image'],
    ]],
    ['.ron-instagram', [['.ron-ig-head h2', 'headline'], ['.ron-ig-tile', 'image']]],
    ['.ron-heritage', [['.legacy-closing']]],
  ];

  function boot() {
    const main = document.querySelector('body.ron-home #MainContent[data-template="index"]');
    if (!main || window[instanceKey] || !('IntersectionObserver' in window)) return;
    window[instanceKey] = true;
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const seen = new WeakSet();
    const pending = new Map();
    const active = new Map();
    const motionNames = new Set(['ron-home-rise', 'ron-home-headline', 'ron-home-image-settle', 'ron-home-campaign', 'ron-home-rule', 'ron-home-mobile-rise']);
    let observer;
    let controller;

    function finish(node) {
      clearTimeout(active.get(node));
      active.delete(node);
      node.removeAttribute('data-ron-home-enter');
      node.removeAttribute('data-ron-home-sequence');
    }

    function enter(node, settings) {
      pending.delete(node);
      observer?.unobserve(node);
      if (!pending.size) { observer?.disconnect(); observer = null; }
      seen.add(node);
      // A focused link/panel stays stationary. Motion never intercepts input.
      if (reducedMotion.matches || document.hidden || node.contains(document.activeElement)) return;
      node.setAttribute('data-ron-home-sequence', String(settings.sequence));
      node.setAttribute('data-ron-home-enter', settings.type);
      // CSS end/cancel is the normal cleanup. This bounded fallback also handles
      // a missing stylesheet or a section hidden mid-animation by its native tabs.
      active.set(node, setTimeout(() => finish(node), 1250));
    }

    function ensureObserver() {
      if (observer || reducedMotion.matches) return;
      observer = new IntersectionObserver(entries => {
        for (const entry of entries) {
          const settings = pending.get(entry.target);
          if (!settings) continue;
          if (!entry.target.isConnected) {
            pending.delete(entry.target);
            observer?.unobserve(entry.target);
          } else if (entry.isIntersecting && entry.intersectionRatio >= .08) enter(entry.target, settings);
        }
      }, { threshold: .08, rootMargin: '0px 0px -5% 0px' });
    }

    function register(scope) {
      if (!(scope instanceof Element || scope instanceof Document)) return;
      for (const [selector, targets] of sections) {
        const roots = [...scope.querySelectorAll(`.ron-port${selector}`)];
        if (scope.matches?.(`.ron-port${selector}`)) roots.unshift(scope);
        for (const root of roots) {
          if (!main.contains(root)) continue;
          for (const [targetSelector, type = 'rise', sequence = 0] of targets) {
            for (const node of root.querySelectorAll(targetSelector)) {
              if (seen.has(node) || pending.has(node)) continue;
              if (reducedMotion.matches || document.hidden) { seen.add(node); continue; }
              ensureObserver();
              pending.set(node, { type, sequence });
              observer?.observe(node);
            }
          }
        }
      }
    }

    function unload(scope) {
      if (!(scope instanceof Element)) return;
      for (const node of pending.keys()) if (scope === node || scope.contains(node)) {
        observer?.unobserve(node);
        pending.delete(node);
      }
      for (const node of active.keys()) if (scope === node || scope.contains(node)) finish(node);
      if (!pending.size) { observer?.disconnect(); observer = null; }
    }

    function settleAll() {
      observer?.disconnect();
      observer = null;
      for (const node of pending.keys()) seen.add(node);
      pending.clear();
      for (const node of active.keys()) finish(node);
    }

    function start() {
      controller?.abort();
      controller = new AbortController();
      const { signal } = controller;
      register(main);
      document.addEventListener('shopify:section:load', event => register(event.target), { signal });
      document.addEventListener('shopify:section:unload', event => unload(event.target), { signal });
      document.addEventListener('animationend', event => {
        if (motionNames.has(event.animationName)) finish(event.target);
      }, { signal });
      document.addEventListener('animationcancel', event => {
        if (motionNames.has(event.animationName)) finish(event.target);
      }, { signal });
      document.addEventListener('focusin', event => {
        for (const node of active.keys()) if (node.contains(event.target)) finish(node);
      }, { signal });
      reducedMotion.addEventListener('change', () => {
        if (reducedMotion.matches) settleAll();
        else register(main); // Already seen content never replays when preference changes.
      }, { signal });
      document.addEventListener('visibilitychange', () => { if (document.hidden) settleAll(); }, { signal });
    }

    start();
    window.addEventListener('pagehide', () => { controller?.abort(); settleAll(); });
    window.addEventListener('pageshow', event => { if (event.persisted) start(); });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();

// A brief first-arrival signature. This enhancement never gates the storefront.
(() => {
  const introKey = 'ron.home.wordmark.v1';
  const fallbackLogo = 'data:image/png;base64,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';
  const instanceKey = Symbol.for('ron.home.wordmark');

  function introduce() {
    if (!document.querySelector('body.ron-home #MainContent[data-template="index"]') || window[instanceKey]) return;
    window[instanceKey] = true;
    const preference = matchMedia('(prefers-reduced-motion: reduce)');
    try {
      if (sessionStorage.getItem(introKey)) return;
      // Record the visit even when motion is skipped, so it cannot replay later.
      sessionStorage.setItem(introKey, 'seen');
    } catch { return; } // Privacy/storage restrictions must never delay entry.
    if (preference.matches || document.hidden || window.Shopify?.designMode) return;
    if (performance.getEntriesByType('navigation')[0]?.type === 'back_forward') return;
    try { if (document.referrer && new URL(document.referrer).origin === location.origin) return; } catch {}
    if (document.activeElement && document.activeElement !== document.body && document.activeElement !== document.documentElement) return;

    const source = document.querySelector('.ron-native-logo__image');
    if (!(source instanceof HTMLImageElement)) return;
    // The fallback embeds the exact approved theme mark, so a cold header request
    // cannot delay entry or turn the signature into a blank loading screen.
    const overlay = document.createElement('div');
    overlay.className = 'ron-home-intro';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.style.pointerEvents = 'none';
    const mark = source.cloneNode(false);
    mark.removeAttribute('id');
    mark.removeAttribute('srcset');
    mark.removeAttribute('sizes');
    mark.src = source.complete && source.naturalWidth ? source.currentSrc || source.src : fallbackLogo;
    mark.alt = '';
    mark.className = 'ron-home-intro__mark';
    overlay.append(mark);

    const controller = new AbortController();
    const { signal } = controller;
    let fallback;
    function finish() {
      clearTimeout(fallback);
      controller.abort();
      overlay.remove();
    }
    // Independent of CSS animation completion, image loading or network state.
    fallback = setTimeout(finish, 1100);
    overlay.addEventListener('animationend', event => {
      if (event.target === overlay && event.animationName === 'ron-home-intro-away') finish();
    }, { signal });
    overlay.addEventListener('animationcancel', finish, { signal });
    preference.addEventListener('change', () => { if (preference.matches) finish(); }, { signal });
    document.addEventListener('visibilitychange', () => { if (document.hidden) finish(); }, { signal });
    // Input reaches the already rendered page and dismisses the signature early.
    document.addEventListener('pointerdown', finish, { signal, capture: true, once: true });
    document.addEventListener('keydown', finish, { signal, capture: true, once: true });
    window.addEventListener('pagehide', finish, { signal, once: true });
    document.body.append(overlay);
    if (getComputedStyle(overlay).position !== 'fixed') finish(); // Missing CSS fails open.
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', introduce, { once: true });
  else introduce();
})();
